// frontend/src/pages/ResetPassword.jsx
import React, { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import './ResetPassword.css';

// Get API URL from environment variable
const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8080";

function ResetPassword() {
  const navigate = useNavigate();
  const { token } = useParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const validatePassword = (pwd) => {
    const errors = [];
    if (pwd.length < 8)                    errors.push("at least 8 characters");
    if (!/[A-Z]/.test(pwd))               errors.push("at least 1 uppercase letter");
    if (!/[a-z]/.test(pwd))               errors.push("at least 1 lowercase letter");
    if (!/[0-9]/.test(pwd))               errors.push("at least 1 number");
    if (!/[!@#$%^&*]/.test(pwd))          errors.push("at least 1 special character (!@#$%^&*)");
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const pwdErrors = validatePassword(password);
    if (pwdErrors.length > 0) {
      setError("Password must have: " + pwdErrors.join(", ") + ".");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_BASE_URL}/api/auth/reset-password/${token}`, { password });
      setSuccess('Password reset successful! Redirecting to login...');
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="reset-page">
      <div className="reset-card">
        {/* Logo */}
        <Link to="/" className="reset-logo-link">
          <img src="/images/logo/Logo-NoBackground.png" alt="Catherine's Oasis" className="reset-logo" />
        </Link>

        {/* Header */}
        <div className="reset-header">
          <h1 className="reset-title">Create new password</h1>
          <p className="reset-subtitle">Enter your new password below</p>
        </div>

        {/* Success Message - just green text */}
        {success && <div className="reset-success">{success}</div>}

        {/* Error Message - just red text */}
        {error && <div className="reset-error">{error}</div>}

        {/* Form */}
        {!success && (
          <form onSubmit={handleSubmit} className="reset-form">
            {/* Password Field */}
            <div className="form-group">
              <div className="password-wrapper input-icon-wrapper">
                <i className="fa-solid fa-lock input-icon" />
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="New password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="reset-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="eye-button"
                >
                  {showPassword ? (
                    <svg className="eye-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  ) : (
                    <svg className="eye-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Confirm Password Field */}
            <div className="form-group">
              <div className="password-wrapper input-icon-wrapper">
                <i className="fa-solid fa-lock input-icon" />
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="reset-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="eye-button"
                >
                  {showConfirmPassword ? (
                    <svg className="eye-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  ) : (
                    <svg className="eye-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Password Requirements Note */}
            <p className="reset-note">
              Password must be at least 8 characters with at least 1 uppercase, 1 lowercase, 1 number, and 1 special character (!@#$%^&*).
            </p>

            <button 
              type="submit" 
              disabled={loading}
              className={`reset-submit-btn ${loading ? 'disabled' : ''}`}
            >
              {loading ? 'Resetting...' : 'Reset password'}
            </button>
          </form>
        )}

        {/* Back to Login */}
        <div className="reset-footer">
          <p>
            Remember your password?{' '}
            <Link to="/login">Back to login</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;